import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { WeaveLogo } from "@/components/WeaveLogo";
import { MaskedLine, EASE } from "@/components/Motion";
import { getQuestions, startAssessment, submitAssessment } from "@/lib/api";

const AGE_BANDS = ["Under 40", "40–49", "50–59", "60+"];
const SITUATIONS = ["Employed full-time", "Employed part-time", "In transition", "Self-employed", "Other"];

const ContactStep = ({ form, setForm, onContinue, busy }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    className="max-w-xl w-full"
    data-testid="contact-step"
  >
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-weave-teal">Before we begin</p>
    <h1 className="mt-4 text-3xl md:text-4xl font-light tracking-tight text-weave-ink leading-tight">
      A few details — <span className="text-weave-teal">all optional</span>.
    </h1>
    <p className="mt-4 text-base text-weave-slate leading-relaxed">
      Your email is used only to send or save your report. Marketing consent is separate, and
      entirely your choice.
    </p>
    <div className="mt-8 border-l-2 border-weave-teal bg-weave-sand/60 rounded-r-xl p-5" data-testid="honesty-note">
      <p className="text-sm font-medium text-weave-ink">This is not a test.</p>
      <p className="mt-2 text-sm text-weave-slate leading-relaxed">
        There are no right or wrong answers, and nothing here is judged. The kindest thing you can
        do for yourself is answer honestly — as things genuinely are, not as you wish they were.
        The more genuine your responses, the more accurate and useful your snapshot will be.
      </p>
    </div>

    <div className="mt-10 space-y-5">
      <div>
        <label htmlFor="first-name" className="block text-sm font-medium text-weave-ink mb-2">First name</label>
        <input
          id="first-name"
          type="text"
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          placeholder="So your report feels personal"
          data-testid="contact-first-name-input"
          className="w-full rounded-xl border border-weave-line bg-white px-4 py-3.5 text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
        />
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-weave-ink mb-2">Email</label>
        <input
          id="email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Only to send you the report"
          data-testid="contact-email-input"
          className="w-full rounded-xl border border-weave-line bg-white px-4 py-3.5 text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="age-band" className="block text-sm font-medium text-weave-ink mb-2">Age band</label>
          <select
            id="age-band"
            value={form.age_band}
            onChange={(e) => setForm({ ...form, age_band: e.target.value })}
            data-testid="contact-age-band-select"
            className="w-full rounded-xl border border-weave-line bg-white px-4 py-3.5 text-weave-ink focus:border-weave-teal focus:outline-none transition-colors duration-200"
          >
            <option value="">Prefer not to say</option>
            {AGE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="situation" className="block text-sm font-medium text-weave-ink mb-2">Current situation</label>
          <select
            id="situation"
            value={form.situation}
            onChange={(e) => setForm({ ...form, situation: e.target.value })}
            data-testid="contact-situation-select"
            className="w-full rounded-xl border border-weave-line bg-white px-4 py-3.5 text-weave-ink focus:border-weave-teal focus:outline-none transition-colors duration-200"
          >
            <option value="">Prefer not to say</option>
            {SITUATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none pt-2" data-testid="contact-consent-label">
        <input
          type="checkbox"
          checked={form.marketing_consent}
          onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
          data-testid="contact-consent-checkbox"
          className="mt-1 h-4 w-4 rounded border-weave-line accent-[#4aa0a4]"
        />
        <span className="text-sm text-weave-slate leading-relaxed">
          I'd like The Weave to keep me posted about the Founder Academy and related programmes.
        </span>
      </label>
      <p className="text-xs text-weave-mute pt-1">
        How we look after your data:{" "}
        <a
          href="https://www.wearetheweave.co.uk/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-privacy-link"
          className="underline text-weave-teal hover:text-weave-tealdeep transition-colors duration-200"
        >
          read our Privacy Notice
        </a>
        .
      </p>
    </div>

    <button
      onClick={onContinue}
      disabled={busy}
      data-testid="contact-continue-button"
      className="group mt-10 inline-flex items-center gap-3 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-8 py-4 font-medium tracking-wide disabled:opacity-60"
    >
      {busy ? <Loader2 size={18} className="animate-spin" /> : null}
      Begin the Index
      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  </motion.div>
);

const QuestionScreen = ({ question, idx, total, answers, onAnswer, onBack }) => {
  const answered = Object.keys(answers).length;
  const progress = Math.round((answered / total) * 100);
  const selected = answers[question.id];
  const SCALE_SHORT = { 1: "Strongly disagree", 2: "Disagree", 3: "Neither", 4: "Agree", 5: "Strongly agree" };

  return (
    <div className="w-full max-w-4xl" data-testid="question-screen">
      <div className="flex items-center justify-between text-xs font-medium text-weave-mute mb-3">
        <span data-testid="question-counter">Question {idx + 1} of {total}</span>
        <span>{progress}% complete</span>
      </div>
      <div className="h-1.5 w-full bg-weave-sand rounded-full overflow-hidden mb-12" data-testid="progress-bar">
        <motion.div
          className="h-full bg-weave-yellow rounded-full"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={question.id}
          initial={{ x: 64, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -64, opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-weave-teal" data-testid="question-section-label">
            Capacity {question.capIndex + 1} of 7 — {question.capName}
          </p>
          <h2 className="mt-6 text-3xl md:text-4xl lg:text-[2.75rem] font-light tracking-tight text-weave-ink leading-[1.2]" data-testid="question-text">
            {question.text}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 md:gap-4 mt-12" role="radiogroup" aria-label="Agreement scale">
            {[1, 2, 3, 4, 5].map((v) => {
              const isSelected = selected === v;
              return (
                <button
                  key={v}
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => onAnswer(v)}
                  data-testid={`answer-option-${v}`}
                  className={`flex sm:flex-col items-center sm:justify-center gap-3 sm:gap-2 rounded-2xl border-2 px-5 py-4 sm:h-28 transition-all duration-200 ${
                    isSelected
                      ? "border-weave-teal bg-weave-teal text-white shadow-[0_8px_30px_rgba(74,160,164,0.3)] sm:scale-[1.03]"
                      : "border-weave-line bg-white text-weave-ink hover:border-weave-teal/50 hover:bg-weave-cream"
                  }`}
                >
                  <span className={`text-2xl font-bold ${isSelected ? "text-white" : "text-weave-teal"}`}>{v}</span>
                  <span className={`text-xs font-medium text-center leading-snug ${isSelected ? "text-white/90" : "text-weave-slate"}`}>
                    {SCALE_SHORT[v]}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-12 flex items-center justify-between">
        <button
          onClick={onBack}
          disabled={idx === 0}
          data-testid="question-back-button"
          className="inline-flex items-center gap-2 text-weave-slate hover:text-weave-ink transition-colors duration-200 text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <p className="text-xs text-weave-mute max-w-[220px] text-right">
          Answer for the last three months — honestly, not ideally.
        </p>
      </div>
    </div>
  );
};

export default function Assessment() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading");
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [assessmentId, setAssessmentId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    first_name: params.get("name") || "",
    email: params.get("email") || "",
    age_band: "",
    situation: "",
    marketing_consent: false,
  });

  useEffect(() => {
    getQuestions()
      .then((d) => {
        setData(d);
        setPhase("contact");
      })
      .catch(() => toast.error("Could not load the assessment. Please refresh."));
  }, []);

  const flat = useMemo(() => {
    if (!data) return [];
    return data.capacities.flatMap((cap, capIndex) =>
      cap.questions.map((q) => ({ ...q, capIndex, capName: cap.name, capId: cap.id }))
    );
  }, [data]);

  const handleContinue = async () => {
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name || null,
        email: form.email || null,
        age_band: form.age_band || null,
        situation: form.situation || null,
        marketing_consent: form.marketing_consent,
      };
      const res = await startAssessment(payload);
      setAssessmentId(res.assessment_id);
      setPhase("quiz");
    } catch {
      toast.error("Something went wrong starting the assessment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const finish = async (finalAnswers) => {
    setPhase("submitting");
    try {
      await submitAssessment(assessmentId, finalAnswers);
      navigate(`/results/${assessmentId}`);
    } catch {
      toast.error("Could not save your results. Please try again.");
      setPhase("quiz");
    }
  };

  const handleAnswer = (value) => {
    const q = flat[idx];
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    setTimeout(() => {
      if (idx === flat.length - 1) {
        finish(next);
      } else {
        setIdx(idx + 1);
      }
    }, 300);
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="assessment-page">
      <header className="border-b border-weave-line bg-weave-cream/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Back to home"><WeaveLogo /></Link>
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-weave-mute">Founder Capacity Index</span>
        </div>
      </header>

      <main className="flex-1 flex items-start md:items-center justify-center px-6 md:px-12 py-16 md:py-20">
        {phase === "loading" && (
          <div className="flex flex-col items-center gap-4 text-weave-slate" data-testid="assessment-loading">
            <Loader2 className="animate-spin" size={28} />
            <p className="text-sm">Preparing your Index…</p>
          </div>
        )}

        {phase === "contact" && (
          <ContactStep form={form} setForm={setForm} onContinue={handleContinue} busy={busy} />
        )}

        {phase === "quiz" && flat.length > 0 && (
          <QuestionScreen
            question={flat[idx]}
            idx={idx}
            total={flat.length}
            answers={answers}
            onAnswer={handleAnswer}
            onBack={() => setIdx(Math.max(0, idx - 1))}
          />
        )}

        {phase === "submitting" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center gap-5 text-center"
            data-testid="assessment-submitting"
          >
            <Loader2 className="animate-spin text-weave-teal" size={32} />
            <MaskedLine className="text-2xl md:text-3xl font-light text-weave-ink">
              Weaving your capacities into a snapshot…
            </MaskedLine>
          </motion.div>
        )}
      </main>
    </div>
  );
}
